import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  GridValueGetter,
  getGridStringOperators,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useOrganizationView } from 'api/aws/organizations_table/useOrganizationView'
import { useUserItems } from 'api/aws/users/useUserItems'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import { customNumericSortOperator, customSortOperator } from 'components/DataGrid/operators/customSortOperator'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import RenderDateCell from 'components/Deals/grid/cells/RenderDateCell'
import RenderDealNameCell from 'components/Deals/grid/cells/RenderDealNameCell'
import RenderDealStatusCell from 'components/Deals/grid/cells/RenderDealStatusCell'
import RenderDealTypeCell from 'components/Deals/grid/cells/RenderDealTypeCell'
import RenderUserCell from 'components/Deals/grid/cells/RenderUserCell'
import AggregateCurrencyCell from 'components/MTB/grid/cells/AggregateCell'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { User } from 'types'

const TERMINAL_STATUSES = ['Ended', 'Cancelled', 'Replaced', 'Canceled']

const defaultSortModel: GridSortModel = [
  // { field: 'lastModified', sort: 'desc' },
  { field: 'creation_timestamp', sort: 'desc' },
  { field: 'name', sort: 'asc' },
  { field: 'organizations', sort: 'asc' },
  { field: 'status', sort: 'asc' },
]
const defaultFilterModel: GridFilterModel = { items: [], quickFilterValues: [] }
export const initialVisibilityState: GridColumnVisibilityModel = {
  deal_uuid: false,
  predecessorId: false,
  successorId: false,
  oldStatus: false,
  SEO: false,
  organizations: false,
  organization: false,
  implementationFee: false,
  lastModified: false,
  contractId: false,
  isGoogleDisplay: false,
  isGoogleSearch: false,
  isLinkedIn: false,
  isMetaAdvertising: false,
  isMicrosoftAdvertising: false,
  isSimplifi: false,
  isSnapchat: false,
  isSpotify: false,
  isTikTok: false,
  isYoutube: false,
  isSeo1: false,
  isSeo2: false,
  isSeo3: false,
  isSeo4: false,
  seoTier: false,
  probability: false,
  weightedTotal: false,
  orgNameReference: false,
  noSignature: false,
  docName: false,
  docStatus: false,
  docTimeCreation: false,
  timeCompleted: false,
  lastDateOfService: false,
  contractedBy: false,
  status: false,
  daysLeft: false,
  __tree_data_group__: false,
}

const BOX_PROPS = {
  display: 'flex',
  height: '100%',
  alignItems: 'center',
  width: '100%',
  backgroundColor: 'transparent',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
}
const OrgDealInfo = () => {
  const { PK } = useParams()
  const apiRef = useGridApiRef()
  const navigate = useNavigate()
  const { pinned } = useLayoutContext()

  const { organization, isParent, viewDeals, viewProducts } = useOrganizationView(PK)
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  // Join products onto deals (parent-safe because viewProducts is already “scoped”)
  const dealsWithProducts = useMemo(() => {
    if (!Array.isArray(viewDeals) || !Array.isArray(viewProducts)) return []

    // optional perf: build product map by deal id once
    const productsByDealId = new Map<string, any[]>()
    for (const p of viewProducts) {
      const id = String(p?.deal_uuid ?? p?.dealId ?? '')
      if (!id) continue
      const arr = productsByDealId.get(id) ?? []
      arr.push(p)
      productsByDealId.set(id, arr)
    }

    return viewDeals.map(deal => {
      const dealId = String(deal?.deal_uuid ?? deal?.dealId ?? '')
      const ownerId =
        deal?.owner && Array.isArray(deal?.owner)
          ? deal?.owner?.[0]
          : deal?.owner && !Array.isArray(deal?.owner)
          ? deal?.owner
          : null
      return {
        ...deal,
        rowId: deal?.SK,
        owner: users?.find((u: User) => u?.user_uuid === ownerId),
        organization: organization,
        // products: productsByDealId.get(dealId) ?? [],
      }
    })
  }, [viewDeals, viewProducts])

  const formatDateString = (date: any) => {
    if (!date) return ''
    return dayjs.isDayjs(date) ? date.format('MM/DD/YYYY') : dayjs(date).format('MM/DD/YYYY')
  }
  const densityStyles = getDensityStyles('standard')
  const columnMap = [
    {
      field: 'name',
      headerName: 'Deal Name',
      aggregable: false,
      width: 250,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      valueFormatter: (_, row) => {
        const org = row?.organization ?? null
        const value = row?.isParent ? row?.name : `${org?.name}-${row?.name}`
        return value
      },
      sortComparator: customSortOperator,
      valueGetter: (_, row) => row?.name,
      renderCell: (params: GridRenderCellParams) => (
        <RenderDealNameCell params={params} navigate={navigate} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      aggregable: false,
      width: 100,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (params: GridValueGetter) => params,
      renderCell: (params: GridRenderCellParams) => (
        <RenderDealTypeCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'dealStatus',
      headerName: 'Deal Status',
      aggregable: false,
      width: 150,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (params: GridValueGetter) => params,
      renderCell: (params: GridRenderCellParams) => <RenderDealStatusCell params={params} />,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      aggregable: false,
      width: 100,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      aggregable: false,
      width: 100,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'netTotal',
      headerName: 'Mo. Total',
      aggregable: true,
      type: 'number',
      width: 125,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customNumericSortOperator,
      // valueGetter: (_, row) => parseFloat(row?.netTotal ?? '0'),
      valueGetter: (_, row) => {
        return !TERMINAL_STATUSES?.includes(row?.dealStatus)
          ? parseFloat(row?.totals?.monthlyTotal || row?.price || '0')
          : 0.0
      },
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'total',
      headerName: 'Total',
      aggregable: true,
      type: 'number',
      width: 100,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) => {
        const isCsa = ['CSA', 'RCSA']?.includes(row?.type) ?? false
        return isCsa
          ? parseFloat((row?.totals?.monthlyTotal || row?.contractTotal) ?? '0')
          : parseFloat((row?.totals?.total || row?.contractTotal) ?? '0')
      },
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'owner',
      headerName: 'Owner',
      aggregable: false,
      width: 75,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (_value, row): GridValueGetter => {
        return row?.owner?.name ?? ''
      },
      renderCell: params => {
        //@ts-ignore
        if (params?.rowNode?.isAutoGenerated) return
        return <RenderUserCell params={params} densityStyles={densityStyles} />
      },
    },
  ] as GridColDef[]
  return (
    <GlobalDataGrid
      rows={dealsWithProducts}
      columns={columnMap}
      apiRef={apiRef}
      context={'org_deals'}
      defaultFilterModel={defaultFilterModel}
      defaultVisibilityModel={initialVisibilityState}
      defaultSortModel={defaultSortModel}
      defaultGridDensity={'standard'}
      checkboxSelection={false}
      aggregationModel={{
        total: 'sum',
        netTotal: 'sum',
        weightedTotal: 'sum',
        implementationFee: 'sum',
        activeProducts: 'sum',
        probability: 'avg',
      }}
      gridContainerSx={{
        position: 'relative',
        display: 'flex',
        maxHeight: '100vh',
        width: '100%',
        justifyContent: 'center',
        transition: 'all 0.3s ease-in-out',
        height: '100%',
      }}
      treeData={false}
    />
  )
}

export default OrgDealInfo
